/**
 * Admin — Users management.
 * Lists Firestore `users/` docs and lets admins ban/unban and delete
 * profile docs. Deleting the auth account itself requires the Admin SDK
 * (server-side), so this UI only manages the Firestore profile + the
 * `banned` flag, which our auth provider enforces on sign-in.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Users, Shield, Ban, CheckCircle2, Trash2, Search, Loader2 } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { useUserAuth } from "@/lib/user-auth";
import { useAuth } from "@/lib/auth";
import {
  adminDeleteUserDoc,
  adminListUsers,
  adminSetUserBanned,
} from "@/lib/firebase-data";

export const Route = createFileRoute("/admin/users")({
  head: () => ({
    meta: [
      { title: "إدارة المستخدمين — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminUsersPage,
});

type UserRow = {
  id: string;
  email?: string;
  displayName?: string;
  username?: string;
  avatar?: string | null;
  banned?: boolean;
};

function AdminUsersPage() {
  const { user, isAdmin: fbAdmin, loading: authLoading } = useUserAuth();
  const { isAuthed: localAdmin } = useAuth();
  // Admin if EITHER Firebase recognizes the email as admin OR the local
  // admin login (SHA-256 hash) is active. This guarantees the page opens
  // even if the Firebase mirror sign-in hasn't completed yet.
  const isAdmin = fbAdmin || localAdmin;
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate({ to: "/" });
      return;
    }
    void refresh();
  }, [authLoading, user, isAdmin]);

  async function refresh() {
    setLoading(true);
    const list = (await adminListUsers()) as UserRow[];
    setUsers(list);
    setLoading(false);
  }

  async function toggleBan(u: UserRow) {
    await adminSetUserBanned(u.id, !u.banned);
    setUsers((arr) => arr.map((x) => (x.id === u.id ? { ...x, banned: !u.banned } : x)));
  }

  async function remove(u: UserRow) {
    if (!confirm(`حذف بيانات المستخدم ${u.email ?? u.id}؟`)) return;
    await adminDeleteUserDoc(u.id);
    setUsers((arr) => arr.filter((x) => x.id !== u.id));
  }

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      u.email?.toLowerCase().includes(s) ||
      u.displayName?.toLowerCase().includes(s) ||
      u.username?.toLowerCase().includes(s)
    );
  });

  if (!isAdmin) return null;

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl" style={{ background: "var(--gradient-primary)" }}>
            <Users className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="truncate text-lg sm:text-2xl font-extrabold">إدارة المستخدمين</h1>
            <p className="text-xs text-muted-foreground">{users.length} مستخدم</p>
          </div>
          <BackToDashboard />
        </div>

        <div className="mb-4 relative max-w-md">
          <Search className="h-4 w-4 absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث بالبريد أو الاسم…"
            className="w-full ps-9 pe-3 py-2.5 rounded-xl bg-card border border-border focus:border-primary focus:outline-none text-sm"
          />
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 mx-auto animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">لا يوجد مستخدمون</div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((u) => (
                <li key={u.id} className="p-3 sm:p-4 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-muted grid place-items-center text-sm font-bold shrink-0" style={{ background: u.avatar ? undefined : "var(--gradient-primary)", color: "white" }}>
                    {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : (u.displayName?.[0] ?? u.email?.[0] ?? "U").toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold truncate flex items-center gap-2">
                      {u.displayName ?? u.username ?? u.email ?? u.id}
                      {u.banned && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-500 border border-red-500/30">محظور</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => toggleBan(u)} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border ${u.banned ? "bg-green-500/10 text-green-600 border-green-500/30 hover:bg-green-500/20" : "bg-yellow-500/10 text-yellow-600 border-yellow-500/30 hover:bg-yellow-500/20"}`}>
                      {u.banned ? <><CheckCircle2 className="h-3.5 w-3.5" /> فك الحظر</> : <><Ban className="h-3.5 w-3.5" /> حظر</>}
                    </button>
                    <button onClick={() => remove(u)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive hover:text-destructive-foreground">
                      <Trash2 className="h-3.5 w-3.5" /> حذف
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground mt-4 flex items-center gap-1.5">
          <Shield className="h-3.5 w-3.5" />
          حذف الحساب نهائياً من Firebase Auth يتطلّب Admin SDK من الخادم — هنا نُحدّث الـ Firestore فقط ونمنع تسجيل الدخول بحقل <code>banned</code>.
        </p>
      </main>
      <Footer />
    </div>
  );
}
